import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { hasJoined, useNewsletterSignup } from '@/hooks/useNewsletterSignup';

// One appearance per browser session; closing the card sets the same key, so
// a dismissal never re-arms (founder spec 2026-09-03).
const SHOWN_SESSION_KEY = 'slideInShown';

// The card must feel like a response to engagement, never an interruption:
// primary path is a farmer-card/news-story click plus 25s on the page;
// fallback is 60% scroll depth. Never before 15s, never on load.
const ENGAGED_DWELL_MS = 25_000;
const MINIMUM_DWELL_MS = 15_000;
const SCROLL_DEPTH_TRIGGER = 0.6;
const SUCCESS_AUTO_CLOSE_MS = 2_200;

const wasShownThisSession = () => {
  try {
    return sessionStorage.getItem(SHOWN_SESSION_KEY) === 'true';
  } catch {
    return false;
  }
};

const markShownThisSession = () => {
  try {
    sessionStorage.setItem(SHOWN_SESSION_KEY, 'true');
  } catch {
    // Storage unavailable - in-memory state still prevents re-show this mount.
  }
};

/* Design-reference styling (founder spec 2026-09-03) - deliberately Oswald,
   unlike the Latest News capture which matches body copy. Do not swap in the
   site's body-text values. z-index 1100 exceeds the highest existing layer
   (modals at 1000). */
const Card = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 340px;
  background: #fff;
  border-top: 4px solid #e89020;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
  padding: 22px;
  z-index: 1100;
  font-family: 'Oswald', sans-serif;
  color: #1c1c1c;
  font-size: 15px;
  line-height: 1.55;

  @media (max-width: 480px) {
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  font-size: 18px;
  color: #888;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
`;

const LineOne = styled.p`
  font-family: inherit;
  font-weight: 500;
  margin: 0 0 6px;
`;

const LineTwo = styled.p`
  font-family: inherit;
  font-weight: 300;
  margin: 0;
`;

const JoinLink = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid #ec008c;
  color: #ec008c;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  font-weight: 600;
  line-height: inherit;
  padding: 0;
`;

const EmailInput = styled.input<{ $invalid: boolean }>`
  border: none;
  border-bottom: 2px solid ${({ $invalid }) => ($invalid ? '#ec008c' : '#999')};
  background: transparent;
  font: inherit;
  width: 180px;
  padding: 0 2px 2px;
  margin-right: 8px;
  outline: none;
  border-radius: 0;

  @media (max-width: 480px) {
    /* 16px minimum so iOS Safari doesn't zoom the page on focus. */
    font-size: 16px;
  }

  &::placeholder {
    color: #999;
  }
`;

const PrivacyNote = styled.div`
  font-family: inherit;
  font-size: 11px;
  color: #777;
  margin-top: 8px;
`;

const ErrorText = styled.div`
  font-family: inherit;
  font-size: 12px;
  color: #ec008c;
  margin-top: 6px;
`;

const SuccessLine = styled.p`
  font-family: inherit;
  font-size: 15px;
  font-weight: 300;
  margin: 0;

  b {
    color: #ec008c;
    font-weight: 600;
  }
`;

interface JoinInSlideInProps {
  /** True once the visitor clicked a farmer card or a news story. */
  engaged: boolean;
  /** Project name for the signup log (the page has no route params). */
  project?: string;
}

export const JoinInSlideIn = ({ engaged, project }: JoinInSlideInProps) => {
  const [show, setShow] = useState(false);
  const [open, setOpen] = useState(false);
  const { email, changeEmail, error, done, submit } = useNewsletterSignup('slide-in', project);
  const mountedAtRef = useRef(Date.now());
  const inputRef = useRef<HTMLInputElement>(null);

  const tryShow = () => {
    if (hasJoined() || wasShownThisSession()) return;
    markShownThisSession();
    setShow(true);
  };

  // Primary trigger: engagement click + 25s total time on the page.
  useEffect(() => {
    if (!engaged) return;
    const elapsed = Date.now() - mountedAtRef.current;
    const timer = setTimeout(tryShow, Math.max(ENGAGED_DWELL_MS - elapsed, 0));
    return () => clearTimeout(timer);
  }, [engaged]);

  // Fallback trigger for readers who don't click: 60% scroll depth, still
  // never before the 15s floor.
  useEffect(() => {
    const onScroll = () => {
      if (Date.now() - mountedAtRef.current < MINIMUM_DWELL_MS) return;
      const depth = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (depth >= SCROLL_DEPTH_TRIGGER) tryShow();
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // Success: show the confirmation briefly, then slide away on our own.
  useEffect(() => {
    if (!(show && done)) return;
    const timer = setTimeout(() => setShow(false), SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [show, done]);

  const dismiss = () => {
    markShownThisSession();
    setShow(false);
  };

  if (!show) return null;

  if (done) {
    return (
      <Card>
        <SuccessLine>
          <b>You are in.</b> The next story from the farm is on its way.
        </SuccessLine>
      </Card>
    );
  }

  return (
    <Card>
      <CloseButton type="button" aria-label="Close" onClick={dismiss}>
        ×
      </CloseButton>
      <LineOne>We are happy you are getting a feel for what our projects are doing.</LineOne>
      <LineTwo>
        If you want to see more of how these projects are making a difference,{' '}
        {open ? (
          <form noValidate onSubmit={submit} style={{ display: 'inline', fontFamily: 'inherit' }}>
            <EmailInput
              ref={inputRef}
              type="email"
              placeholder="your@email.com"
              value={email}
              $invalid={error !== null}
              onChange={(e) => changeEmail(e.target.value)}
              onKeyDown={(e) => {
                // Explicit Enter handling on top of implicit form submission,
                // matching JoinInCapture.
                if (e.key === 'Enter') submit(e);
              }}
            />
            <JoinLink type="submit">join in.</JoinLink>
          </form>
        ) : (
          <JoinLink type="button" onClick={() => setOpen(true)}>
            join in.
          </JoinLink>
        )}
      </LineTwo>
      {open && error === 'invalid' && (
        <ErrorText>That email doesn&apos;t look right — check the address.</ErrorText>
      )}
      {open && error === 'failed' && (
        <ErrorText>Something went wrong — please try again.</ErrorText>
      )}
      {open && <PrivacyNote>Unsubscribe anytime.</PrivacyNote>}
    </Card>
  );
};
